import { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Grid, IconButton, Typography, Box, Avatar, LinearProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Snackbar, Alert, List, ListItem, ListItemText, ClickAwayListener
} from '@mui/material';
import { Close as CloseIcon, LocalShipping as DeliveryIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const emptyForm = {
    Id: '',
    CustomerId: '', CustomerName: '', ContactNumber: '', AddressLine: '',
    DeliveryDate: new Date().toISOString().split('T')[0], DeliveryReturnDate: '', RentDays: '',
    DeliveryOutTime: getCurrentTime(), DeliveryInTime: '',
    BillNumber: '', BillPrefix: '', DeliveryNo: '', FinYear: '',
    Remarks: '', AdvanceAmount: '', items: []
};

const DeliveryOutModal = ({ open, onClose, onSave, deliveryData = null, mode = 'create' }) => {
    const { companyId, branchId, token, userId } = useAuth();
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({ ...emptyForm });

    // Customer search
    const [customers, setCustomers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef(null);

    // Item picker
    const [itemPickerOpen, setItemPickerOpen] = useState(false);
    const [allItems, setAllItems] = useState([]);
    const [pickerSearch, setPickerSearch] = useState('');
    const [selectedPickerItems, setSelectedPickerItems] = useState([]);

    const fetchAllItems = async () => {
        try {
            const res = await fetchData('ItemApi', `/${companyId}`, token);
            if (res?.responseCode === '1111' && res.response_data) setAllItems(res.response_data);
        } catch (e) { console.error('Error fetching items:', e); }
    };

    const filteredPickerItems = allItems.filter(i =>
        i.Name?.toLowerCase().includes(pickerSearch.toLowerCase()) ||
        i.CategoryName?.toLowerCase().includes(pickerSearch.toLowerCase())
    );

    const togglePickerItem = (item) => {
        setSelectedPickerItems(prev =>
            prev.find(p => p.ItemId === item.Id)
                ? prev.filter(p => p.ItemId !== item.Id)
                : [...prev, { ItemId: item.Id, ItemName: item.Name, CategoryName: item.CategoryName, RentRate: item.RentRate, Rate: item.Rate, DeliveryOutQuantity: '1', DeliveryInQuantity: 0, GoodQuantity: 0, DamagedQuantity: 0, HSNCode: item.HSNCode || '', GSTRate: '' }]
        );
    };

    const handleAddSelectedItems = () => {
        setFormData(prev => ({ ...prev, items: [...prev.items, ...selectedPickerItems] }));
        setSelectedPickerItems([]);
        setPickerSearch('');
        setItemPickerOpen(false);
    };

    const openItemPicker = () => {
        setSelectedPickerItems([]);
        setPickerSearch('');
        if (allItems.length === 0) fetchAllItems();
        setItemPickerOpen(true);
    };

    const filteredCustomers = customerSearch.length > 0
        ? customers.filter(c => c.Name?.toLowerCase().includes(customerSearch.toLowerCase()))
        : [];

    const fetchCustomers = async () => {
        try {
            const res = await fetchData('CustomerApi', `/${companyId}`, token);
            if (res?.responseCode === '1111' && res.response_data) {
                setCustomers(res.response_data);
            }
        } catch (e) { console.error('Error fetching customers:', e); }
    };

    const handleSelectCustomer = (customer) => {
        setFormData(prev => ({
            ...prev,
            CustomerId: customer.Id,
            CustomerName: customer.Name,
            ContactNumber: customer.ContactNumber || '',
            AddressLine: customer.AddressLine || ''
        }));
        setCustomerSearch(customer.Name);
        setShowDropdown(false);
    };

    const handleCustomerSearchChange = (value) => {
        setCustomerSearch(value);
        setFormData(prev => ({ ...prev, CustomerName: value, CustomerId: '', ContactNumber: '', AddressLine: '' }));
        setShowDropdown(true);
    };

    const fetchBillNumber = async () => {
        try {
            const response = await fetchData('DeliveryOutApi', `/MaxBillno/${companyId}/${branchId}`, token);
            console.log('Bill number response:', response);
            const data = response.response_data || response || {};
            const billNumber = data.BillNumber || data.billNumber || 1;
            const billPrefix = data.BillPrefix || data.billPrefix || '';
            const finYear = data.FinYear || data.finYear || '';
            const DeliveryNo = `${billPrefix}/${String(billNumber)}/${finYear}`;
            console.log('Generated DeliveryNo:', DeliveryNo);
            setFormData(prev => ({
                ...prev,
                BillNumber: billNumber,
                BillPrefix: billPrefix,
                DeliveryNo: DeliveryNo,
                FinYear: finYear
            }));
        } catch (e) { console.error('Error fetching bill number:', e); }
    }; useEffect(() => {
        if (!open) return;
        fetchCustomers();
        if (deliveryData && mode !== 'create') {
            const d = deliveryData;
            const details = deliveryData.DeliveryCallDetails || [];
            let billPrefix = '', billNumber = d.BillNumber || '', finYear = d.FinYear || '';
            if (d.DeliveryNo) {
                const parts = d.DeliveryNo.split('/');
                if (parts.length === 3) { billPrefix = parts[0]; billNumber = parts[1]; finYear = parts[2]; }
            }
            setFormData({
                Id: d.Id || '',
                CustomerId: d.CustomerId || '',
                CustomerName: d.CustomerName || '',
                ContactNumber: d.ContactNumber || '',
                AddressLine: d.AddressLine || '',
                DeliveryDate: d.DeliveryDate ? d.DeliveryDate.split('T')[0] : '',
                DeliveryReturnDate: d.DeliveryReturnDate ? d.DeliveryReturnDate.split('T')[0] : '',
                RentDays: d.RentDays || '',
                DeliveryOutTime: d.DeliveryOutTime || '',
                DeliveryInTime: d.DeliveryInTime || '',
                BillNumber: billNumber,
                DeliveryNo: d.DeliveryNo || '',
                FinYear: finYear,
                Remarks: d.Remarks || '',
                AdvanceAmount: d.AdvanceAmount || '',
                items: details.map(item => ({
                    Id: item.Id || '',
                    ItemId: item.ItemId || '',
                    ItemName: item.ItemName || '',
                    DeliveryOutQuantity: item.DeliveryOutQuantity?.toString() || '0',
                    DeliveryInQuantity: item.DeliveryInQuantity || 0,
                    GoodQuantity: item.GoodQuantity || 0,
                    DamagedQuantity: item.DamagedQuantity || 0,
                    HSNCode: item.HSNCode || '',
                    Rate: item.RentRate || '',
                    GSTRate: item.GSTRate || ''
                }))
            });
            setCustomerSearch(d.CustomerName || '');
        } else {
            setFormData({ ...emptyForm, DeliveryOutTime: getCurrentTime() });
            setCustomerSearch('');
            setErrors({});
            fetchBillNumber();
        }
    }, [open, deliveryData, mode]);

    useEffect(() => {
        if (formData.DeliveryDate && formData.DeliveryReturnDate) {
            const diff = Math.ceil(Math.abs(new Date(formData.DeliveryReturnDate) - new Date(formData.DeliveryDate)) / 86400000);
            setFormData(prev => ({ ...prev, RentDays: diff }));
        }
    }, [formData.DeliveryDate, formData.DeliveryReturnDate]);

    const handleInputChange = (field, value, itemIndex = null) => {
        if (itemIndex !== null) {
            setFormData(prev => {
                const newItems = [...prev.items];
                newItems[itemIndex] = { ...newItems[itemIndex], [field]: value };
                return { ...prev, items: newItems };
            });
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
            if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.DeliveryDate) newErrors.DeliveryDate = 'Required';
        if (!customerSearch.trim()) newErrors.CustomerName = 'Required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        setLoading(true);
        try {
            const deliveryMainData = {
                Id: formData.Id || undefined,
                CustomerId: formData.CustomerId || null,
                CustomerName: formData.CustomerName || customerSearch,
                ContactNumber: formData.ContactNumber,
                AddressLine: formData.AddressLine,
                DeliveryDate: formData.DeliveryDate,
                DeliveryReturnDate: formData.DeliveryReturnDate || null,
                RentDays: formData.RentDays || 0,
                DeliveryOutTime: formData.DeliveryOutTime || null,
                DeliveryInTime: formData.DeliveryInTime || null,
                BillNumber: formData.BillNumber,
                DeliveryNo: formData.DeliveryNo,
                FinYear: formData.FinYear,
                Remarks: formData.Remarks,
                AdvanceAmount: formData.AdvanceAmount || null,
                CompanyId: companyId,
                BranchId: branchId,
                CreatedBy: userId,
                ModifiedBy: userId,
                IsActive: true
            };
            const deliveryDetails = formData.items.map(item => ({
                Id: item.Id || undefined,
                ItemId: item.ItemId,
                ItemName: item.ItemName,
                DeliveryOutQuantity: parseFloat(item.DeliveryOutQuantity) || 0,
                DeliveryInQuantity: item.DeliveryInQuantity || 0,
                GoodQuantity: item.GoodQuantity || 0,
                DamagedQuantity: item.DamagedQuantity || 0,
                HSNCode: item.HSNCode || null,
                Rate: item.RentRate || null,
                GSTRate: item.GSTRate || null,
                IsActive: true
            }));
            const payload = { DeliveryInAndOut: deliveryMainData, DeliveryInAndOutDetails: deliveryDetails };
            let response;
            if (mode === 'edit' && formData.Id) {
                response = await updateData('DeliveryOutApi', formData.Id, payload, token);
            } else {
                response = await postData('DeliveryOutApi', payload, token);
            }
            const isSuccess = response && (response.responseCode === '1111' || response.responseCode === 1111 || response.success === true);
            if (isSuccess) {
                setSuccessMessage(mode === 'edit' ? 'Delivery updated successfully!' : 'Delivery created successfully!');
                setShowSuccess(true);
                setTimeout(() => { onSave(); onClose(); }, 1500);
            }
        } catch (e) { console.error('Error saving delivery:', e); }
        finally { setLoading(false); }
    };

    if (!open) return null;

    const ro = mode === 'view';
    const fSx = { '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' }, '& .MuiInputLabel-root': { fontSize: '0.80rem' } };
    const numCellSx = { width: '90px', '& .MuiInputBase-input': { fontSize: '0.75rem', textAlign: 'right', py: 0.4 }, '& .MuiInputBase-root': { height: '26px' } };

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper"
            PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh', width: '75vw', maxWidth: '900px', mt: 0 } }}>
            <DialogTitle sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', color: 'white', p: 1.0, height: '35px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 24, height: 24 }}>
                            <DeliveryIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {mode === 'view' ? 'View Delivery Out' : mode === 'edit' ? 'Edit Delivery Out' : 'Create Delivery Out'}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: 'white', p: 0.4 }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 1.5, pt: 2, overflowY: 'auto' }}>
                {loading && <LinearProgress sx={{ mb: 1 }} />}
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    {/* Row 1: Delivery No, Customer Name, Contact */}
                    <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Delivery No"
                            value={formData.DeliveryNo}
                            InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Delivery Date *" type="date"
                            value={formData.DeliveryDate}
                            onChange={(e) => handleInputChange('DeliveryDate', e.target.value)}
                            error={!!errors.DeliveryDate} helperText={errors.DeliveryDate}
                            InputProps={{ readOnly: ro }} InputLabelProps={{ shrink: true }}
                            sx={{ ...fSx, '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.2 } }} />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Out Time" type="time"
                            value={formData.DeliveryOutTime}
                            onChange={(e) => handleInputChange('DeliveryOutTime', e.target.value)}
                            InputProps={{ readOnly: ro }} InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField fullWidth size="small" label="Remarks"
                            value={formData.Remarks}
                            onChange={(e) => handleInputChange('Remarks', e.target.value)}
                            InputProps={{ readOnly: ro }} InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>                    

                    <Grid item xs={12} md={4}>
                        <ClickAwayListener onClickAway={() => setShowDropdown(false)}>
                            <Box sx={{ position: 'relative' }} ref={searchRef}>
                                <TextField fullWidth size="small" label="Customer Name *"
                                    value={customerSearch}
                                    onChange={(e) => handleCustomerSearchChange(e.target.value)}
                                    onFocus={() => customerSearch && setShowDropdown(true)}
                                    error={!!errors.CustomerName}
                                    helperText={errors.CustomerName}
                                    InputProps={{ readOnly: ro }}
                                    InputLabelProps={{ shrink: true }}
                                    placeholder="Search or type new..."
                                    sx={{ ...fSx, '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.2 } }} />
                                {showDropdown && filteredCustomers.length > 0 && (
                                    <Paper sx={{ position: 'absolute', zIndex: 9999, width: '100%', maxHeight: 180, overflowY: 'auto', boxShadow: 3 }}>
                                        <List dense disablePadding>
                                            {filteredCustomers.map(c => (
                                                <ListItem key={c.Id} button onClick={() => handleSelectCustomer(c)}
                                                    sx={{ py: 0.5, '&:hover': { bgcolor: '#f3f0ff' } }}>
                                                    <ListItemText
                                                        primary={<Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{c.Name}</Typography>}
                                                        secondary={<Typography sx={{ fontSize: '0.65rem', color: '#666' }}>{c.ContactNumber}</Typography>} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                )}
                            </Box>
                        </ClickAwayListener>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Contact Number"
                            value={formData.ContactNumber}
                            onChange={(e) => handleInputChange('ContactNumber', e.target.value)}
                            InputProps={{ readOnly: ro }} InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" label="Address"
                            value={formData.AddressLine}
                            onChange={(e) => handleInputChange('AddressLine', e.target.value)}
                            InputProps={{ readOnly: ro }} InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Advance Amount" type="number"
                            value={formData.AdvanceAmount}
                            onChange={(e) => handleInputChange('AdvanceAmount', e.target.value)}
                            InputProps={{ readOnly: ro }} InputLabelProps={{ shrink: true }}
                            sx={{ ...fSx, '& .MuiInputBase-input': { fontWeight: 700, textAlign: 'right' } }} />
                    </Grid>

                    {/* Row 2: Delivery Date, Return Date, Rent Days, Out Time */}


                    {/* Items */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.75rem' }}>Items</Typography>
                                {!ro && (
                                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                                        onClick={openItemPicker}
                                        sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.3, px: 1, background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', color: 'white', borderRadius: '6px' }}>
                                        Add Item
                                    </Button>
                                )}
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                            {['Item Name', 'Category', 'Out Qty', 'Rent Rate'].map(h => (
                                                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1.2, px: 2, textAlign: h === 'Item Name' || h === 'Category' ? 'left' : 'right' }}>{h}</TableCell>
                                            ))}
                                            {!ro && <TableCell sx={{ width: 40 }} />}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {formData.items.map((item, index) => (
                                            <TableRow key={index} sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                                                <TableCell sx={{ py: 1, px: 2 }}>
                                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{item.ItemName}</Typography>
                                                </TableCell>
                                                <TableCell sx={{ py: 1, px: 2 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>{item.CategoryName || ''}</Typography>
                                                </TableCell>
                                                {['DeliveryOutQuantity'].map(f => (
                                                    <TableCell key={f} sx={{ py: 1, px: 2, textAlign: 'right' }}>
                                                        <TextField type="number" value={item[f] ?? 0} size="small"
                                                            onChange={(e) => handleInputChange(f, e.target.value, index)}
                                                            InputProps={{ readOnly: ro }} sx={numCellSx} />
                                                    </TableCell>
                                                ))}
                                                {['RentRate'].map(f => (
                                                    <TableCell key={f} sx={{ py: 1, px: 2, textAlign: 'right' }}>
                                                        <TextField value={item[f] ?? ''} size="small"
                                                            onChange={(e) => handleInputChange(f, e.target.value, index)}
                                                            InputProps={{ readOnly: ro }} sx={numCellSx} />
                                                    </TableCell>
                                                ))}
                                                {!ro && (
                                                    <TableCell sx={{ py: 1, px: 1.5, textAlign: 'center' }}>
                                                        <IconButton size="small"
                                                            onClick={() => setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }))}
                                                            sx={{ color: '#ef4444', p: 0.5 }}>
                                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 1, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" disabled={loading}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', py: 0.4, px: 1.5 }}>
                    {ro ? 'Close' : 'Cancel'}
                </Button>
                {!ro && (
                    <Button onClick={handleSave} variant="contained" disabled={loading}
                        sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', py: 0.4, px: 1.5 }}>
                        {loading ? (mode === 'edit' ? 'Updating...' : 'Saving...') : (mode === 'edit' ? 'Update' : 'Save')}
                    </Button>
                )}
            </DialogActions>

            <Snackbar open={showSuccess} autoHideDuration={1500} onClose={() => setShowSuccess(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setShowSuccess(false)} severity="success"
                    sx={{ width: '100%', fontSize: '0.9rem', fontWeight: 600 }}>
                    {successMessage}
                </Alert>
            </Snackbar>
        </Dialog>

        {/* Item Picker Dialog */}
        <Dialog open={itemPickerOpen} onClose={() => setItemPickerOpen(false)} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 2, maxHeight: '80vh' } }}>
            <DialogTitle sx={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', color: 'white', p: 1.2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Select Items</Typography>
                    <IconButton onClick={() => setItemPickerOpen(false)} sx={{ color: 'white', p: 0.3 }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 1.5, pt: 2 }}>
                <TextField fullWidth size="small" placeholder="Search by item or category..."
                    value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)}
                    sx={{ mb: 1.5, '& .MuiInputBase-root': { fontSize: '0.8rem', height: '32px' } }} />
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 1, maxHeight: '50vh', overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                <TableCell padding="checkbox" sx={{ fontWeight: 700, py: 1.2, px: 1.5 }} />
                                {['Category', 'Item Name', 'Rent Rate', 'Sale Rate'].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1.2, px: 2 }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPickerItems.map(item => {
                                const selected = !!selectedPickerItems.find(p => p.ItemId === item.Id);
                                return (
                                    <TableRow key={item.Id} onClick={() => togglePickerItem(item)}
                                        sx={{ cursor: 'pointer', bgcolor: selected ? '#f3f0ff' : 'inherit', '&:hover': { bgcolor: '#f3f0ff' } }}>
                                        <TableCell padding="checkbox" sx={{ py: 1, px: 1.5 }}>
                                            <input type="checkbox" checked={selected} onChange={() => togglePickerItem(item)} />
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.78rem', py: 1, px: 2, color: '#555' }}>{item.CategoryName || '-'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.78rem', py: 1, px: 2, fontWeight: 500 }}>{item.Name}</TableCell>
                                        <TableCell sx={{ fontSize: '0.78rem', py: 1, px: 2, textAlign: 'right' }}>₹{item.RentRate ?? 0}</TableCell>
                                        <TableCell sx={{ fontSize: '0.78rem', py: 1, px: 2, textAlign: 'right' }}>₹{item.Rate ?? 0}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions sx={{ p: 1, gap: 1, borderTop: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#666', flex: 1, pl: 1 }}>
                    {selectedPickerItems.length} item(s) selected
                </Typography>
                <Button onClick={() => setItemPickerOpen(false)} variant="outlined" size="small"
                    sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px' }}>
                    Cancel
                </Button>
                <Button onClick={handleAddSelectedItems} variant="contained" size="small"
                    disabled={selectedPickerItems.length === 0}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px', background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}>
                    Add to List
                </Button>
            </DialogActions>
        </Dialog>
    </>
    );
};

export default DeliveryOutModal;

